export const translations = {
    en: {
        nav: {
            home: 'Home',
            topics: 'Topics',
            grammar: 'Grammar',
            wordbank: 'Word bank',
            games: 'Games',
        },
        home: {
            greeting: 'Hello',
            whatToExplore: 'What will you explore today?',
            yourTopics: 'Your topics',
            seeAll: 'See all',
            addLanguage: 'Add language',
            luna: {
                title: 'Practice with Gabi',
                subtitle: 'Talk about any topic at your level',
                cta: 'Speak now',
            },
            quickPractice: 'Quick practice',
            vocabulary: 'Vocabulary',
            vocabularySub: 'Review words from your topics',
            sentences: 'Sentences',
            sentencesSub: 'Build & check your grammar',
            pronunciation: 'Pronunciation',
            pronunciationSub: 'Say it right, Gabi listens',
            stories: 'Stories',
            storiesSub: 'Read aloud or listen to Gabi',
        },
        topicNames: {
            greetings: 'Greetings & introductions',
            numbers: 'Numbers & counting',
            family: 'Family & relationships',
            food: 'Food & drink basics',
            home: 'Home & living',
            colours: 'Colours & descriptions',
            shopping: 'Shopping & market',
            gettingAround: 'Getting around',
            dailyRoutine: 'Daily routine',
            weather: 'Weather & seasons',
            health: 'Health & body',
            celebrations: 'Celebrations & events',
            cafe: 'At the cafe',
            travel: 'Travel & transport',
            restaurants: 'Food & restaurants',
            culture: 'Culture & history',
            work: 'Work & career',
            technology: 'Technology & media',
            news: 'News & current events',
            environment: 'Environment & society',
            arts: 'Arts & entertainment',
            lawJustice: 'Law & justice',
            englishGreetings: 'Greetings & introductions',
            englishNumbers: 'Numbers & counting',
            englishCafe: 'At the cafe',
            englishTravel: 'Travel & transport',
            spanishGreetings: 'Saludos e introducciones',
            spanishNumbers: 'Números y cantidades',
            spanishFamily: 'Familia y relaciones',
            spanishFood: 'Comida y restaurantes',
            spanishTravel: 'Viajes y transporte',
            frenchGreetings: 'Salutations',
            frenchNumbers: 'Chiffres et nombres',
            frenchTravel: 'Voyages et transports',
        },
        topicDescs: {
            greetings: 'Say hello, introduce yourself and ask basic questions',
            numbers: 'Count, tell the time and use basic quantities',
            family: 'Talk about your family and the people in your life',
            food: 'Order food, talk about what you like to eat',
            home: 'Describe where you live and the rooms in your home',
            colours: 'Describe people, objects and places simply',
            shopping: 'Buy things, ask prices and describe what you need',
            gettingAround: 'Use public transport and ask for directions',
            dailyRoutine: 'Describe your day and talk about habits',
            weather: 'Talk about the weather and seasonal activities',
            health: 'Describe how you feel and talk to a doctor',
            celebrations: 'Talk about birthdays, holidays and special occasions',
            cafe: 'Order coffee, chat with the barista and read the menu',
            travel: 'Book tickets, navigate airports and talk about trips',
            restaurants: 'Order a full meal, discuss cuisine and make reservations',
            culture: 'Discuss Italian art, history and cultural traditions',
            work: 'Talk about your job, workplace and professional goals',
            technology: 'Discuss social media, apps and digital life',
            englishGreetings: 'Say hello, introduce yourself and ask basic questions',
            englishNumbers: 'Count, tell the time and use basic quantities',
            englishCafe: 'Order coffee, chat and read the menu',
            englishTravel: 'Book tickets and navigate airports',
            spanishGreetings: 'Say hello, introduce yourself and ask basic questions',
            spanishNumbers: 'Count and talk about quantities',
            spanishFamily: 'Talk about your family and relationships',
            spanishFood: 'Order food and talk about restaurants',
            spanishTravel: 'Book tickets and talk about trips',
            frenchGreetings: 'Say hello and introduce yourself',
            frenchNumbers: 'Count and talk about numbers',
            frenchTravel: 'Talk about trips and transport',
        },
        common: {
            save: 'Save',
            cancel: 'Cancel',
            close: 'Close',
            delete: 'Delete',
            loading: 'Loading...',
            continue: 'Continue',
            start: 'Start',
            next: 'Next',
            back: 'Back',
            done: 'Done',
            tryAgain: 'Try again',
        },
        onboarding: {
            targetLanguage: {
                step: 'Step 2 of 5',
                heading: 'Which language do you want to learn?',
                subheading: "We'll build your entire curriculum, lessons and AI conversations around your choice.",
                examNote: '🎓 All languages include full exam preparation from A1 to C2',
                startLearning: 'Start learning',
                chooseLanguage: 'Choose a language',
                speakers: 'speakers',
            },
            goal: {
                step: 'Step 3 of 5',
                heading: 'Why are you learning?',
                subheading: 'Choose up to 3 reasons — this helps Vozoly personalise your lessons and conversation practice.',
                selectAtLeastOne: 'Select at least one',
                selected: 'selected',
                maxReached: '— maximum reached',
                chooseGoal: 'Choose your goal',
                continue: 'Continue',
                goals: {
                    exam: { title: 'Pass an exam', description: 'DELF, DELE, CILS, IELTS, Cambridge and more — I need an official certificate' },
                    travel: { title: 'Travel & holidays', description: 'I want to get by confidently when I visit or live abroad' },
                    work: { title: 'Work & career', description: 'I need the language for my job, business or professional life' },
                    family: { title: 'Family & heritage', description: 'I want to connect with my roots or communicate with family' },
                    culture: { title: 'Culture & enjoyment', description: 'I love the language, music, food or films and want to go deeper' },
                    brain: { title: 'Keep my brain sharp', description: 'Learning a language is one of the best things you can do for your mind' },
                },
            },
            path: {
                step: 'Step 4 of 5',
                heading: 'How do you want to start?',
                subheading: 'We need to know your current level so Vozoly can place you in the right starting point.',
                recommended: 'Recommended',
                testTitle: 'Take the placement test',
                testDesc: 'It only takes 8 to 12 minutes and gives you a precise result across grammar, listening, speaking and writing.',
                testDetail1: '⏱ 8–12 minutes on average',
                testDetail2: '🎯 Precise CEFR placement',
                testDetail3: '📈 Adapts to your answers in real time',
                testDetail4: '✅ Can finish early if your level is clear',
                beginnerTitle: "I'm a complete beginner",
                beginnerDesc: "Never studied this language before? No test needed — we'll start you at A1 and build your foundations from scratch. You'll be speaking your first words today.",
                beginnerDetail1: '🌱 Start from zero — no pressure',
                beginnerDetail2: '🏁 Placed at A1 automatically',
                manualTitle: 'I already know my level',
                manualDesc: 'If you have studied this language before and know your CEFR level, skip the test and choose your starting point directly — A1 through C2.',
                manualDetail1: '⚡ Start immediately',
                manualDetail2: '🔤 A1 · A2 · B1 · B2 · C1 · C2',
                note: '💡 You can always retake the placement test or change your level later from your profile.',
                startTest: 'Start placement test',
                startBeginner: 'Start at A1',
                chooseLevel: 'Choose my level',
                chooseHow: 'Choose how to start',
            },
            placementTest: {
                listeningCheck: 'Listening check',
                speakingCheck: 'Speaking check',
                writingCheck: 'Writing check',
                preparingTest: 'Preparing your test...',
                preparingListening: 'Preparing your listening test...',
                listenCarefully: 'Listen carefully — you\'ll answer {count} questions about the same passage.',
                playAudio: 'Play audio',
                playing: 'Playing...',
                question: 'Question',
                of: 'of',
                listeningError: 'Something went wrong loading the listening test. You can continue without it.',
                continue: 'Continue',
                checkAnswer: 'Check answer',
                nextQuestion: 'Next question',
                abandonTitle: 'Abandon test?',
                abandonDesc: 'Your progress will be lost and you\'ll need to start again from the beginning.',
                keepGoing: 'Keep going',
                abandon: 'Abandon',
                speakingIntro: 'Speak for about 30-60 seconds. Just talk naturally — we\'re listening to how you use the language, not what you say about your level.',
                writingIntro: 'Write a short response below. We\'re looking at how you use the language, not what you say about your level.',
                analyzingSpeaking: 'Analyzing your speaking...',
                analyzingWriting: 'Analyzing your writing...',
                transcribing: 'Transcribing what you said...',
                yourPrompt: 'Your prompt',
                holdToSpeak: 'Hold to speak',
                releaseToStop: 'release to stop',
                slideToLock: 'slide to lock',
                recording: 'Recording',
                skipStep: 'Skip this step',
                gotItSpeaking: 'Got it! Thanks for speaking with us.',
                gotItWriting: 'Got it! Thanks for writing for us.',
                writeHere: 'Write here in the language you\'re learning...',
                submitWriting: 'Submit writing',
                seeMyResult: 'See my final result',
                calibrating: 'Calibrating your level...',
                yourLevel: 'Your level',
                adjustedLevel: 'Adjusted level',
                doesThisFeel: 'Does this feel right?',
                feelsAccurate: 'Yes, this feels accurate',
                lowerLevel: 'I think I\'m a level lower',
                higherLevel: 'I think I\'m a level higher',
                resultDisclaimer: 'This result combines your grammar, listening, speaking and writing performance. Keep practicing with Gabi to fine-tune it further as you go.',
                retakeNote: 'You can retake this test or adjust your level anytime from your profile.',
                adjustedFrom: 'Adjusted from {from} to {to} based on your feedback.',
                changeAnytime: 'You can always change this from your profile.',
                loading: 'Loading...',
                questionNum: 'Question',
                movingOn: 'Moving to next question automatically...',
                changeAnswer: 'Change answer',
                next: 'Next',
                seeMyResultGrammar: 'See my result',
                skipWait: 'Skip wait',
                correct: 'Correct',
                answered: 'Answered',
                placedAt: 'Placed at',
                saveResultsBtn: 'Save results and create account',
            },
            chooseLevel: {
                heading: "What's your current level?",
                subheading: 'Be honest — choosing the right level means your lessons will feel challenging but never overwhelming.',
                note: '💡 Not sure? Take the placement test instead — it only takes 8 to 12 minutes and gives you a precise result.',
                takeTest: 'Take the test',
                saveLevel: 'Save level',
                startAt: 'Start at',
                chooseLevel: 'Choose your level',
                levels: {
                    A1: { label: 'Beginner', desc: 'I know a few words but nothing solid yet' },
                    A2: { label: 'Elementary', desc: 'I can handle simple conversations and basic grammar' },
                    B1: { label: 'Intermediate', desc: 'I can express myself on familiar topics with some confidence' },
                    B2: { label: 'Upper Intermediate', desc: 'I can discuss complex topics and understand most content' },
                    C1: { label: 'Advanced', desc: 'I speak fluently and can handle demanding professional situations' },
                    C2: { label: 'Mastery', desc: 'I have near-native proficiency and understand everything' },
                },
            },
        },
        topics: {
            title: 'Topics',
            searchPlaceholder: 'Search topics...',
            allLevels: 'All levels',
            noTopicsFound: 'No topics found',
            tryDifferentSearch: 'Try a different search term',
            locked: 'Locked',
            levelNames: {
                A1: 'Beginner',
                A2: 'Elementary',
                B1: 'Intermediate',
                B2: 'Upper Intermediate',
                C1: 'Advanced',
                C2: 'Mastery',
            },
        },
        grammar: {
            title: 'Grammar',
            searchPlaceholder: 'Look up any verb...',
            lookUp: 'Look up',
            intro: 'Tap any verb to see its full conjugation across every tense and save forms to your word bank. Don\'t see the verb you\'re after? Search for',
            introBold: 'any',
            introEnd: 'Italian verb above — not just the ones listed below.',
            verbs: 'verbs',
        },
        profile: {
            title: 'Profile',
            myProfile: 'My profile',
            freePlan: 'Free plan',
            active: 'Active',
            tapToSwitch: 'Tap to switch',
            learnMultiple: 'Learn multiple languages — switch between them anytime',
            account: 'Account',
            displayName: 'Display name',
            emailAddress: 'Email address',
            changePassword: 'Change password',
            updatePassword: 'Update your password',
            prefs: 'Preferences',
            notificationsDesc: 'Daily reminders and streak alerts',
            soundEffects: 'Sound effects',
            on: 'On',
            helpSupport: 'Help & support',
            helpSupportDesc: 'FAQs and contact us',
            termsPrivacy: 'Terms & privacy policy',
            termsPrivacyDesc: 'Read our terms of service',
            dangerZone: 'Danger zone',
            deleteMyAccount: 'Delete my account',
            deleteMyAccountDesc: 'Permanently delete your account and all data',
            deleteAccountTitle: 'Delete your account?',
            deleteAccountWarning: 'This will permanently delete your account, all your progress, word bank and conversation history. This cannot be undone.',
            enterPasswordConfirm: 'Enter your password to confirm:',
            yourPassword: 'Your password',
            deleting: 'Deleting...',
            deleteAccountBtn: 'Delete account',
            currentPlanFree: 'You have access to the first 3 topics per level and 2 Gabi sessions per day.',
            unlockEverything: 'Unlock everything',
            unlockDesc: 'Get full access to all topics, unlimited Gabi conversations, exam prep and more.',
            featureAllTopics: 'All topics at every level',
            featureUnlimitedLuna: 'Unlimited Gabi conversations',
            featureExamPrep: 'Full exam preparation',
            featureUnlimitedWordBank: 'Unlimited word bank',
            featureFullStats: 'Full stats & Cambridge tracking',
            seePlans: 'See plans',
            removeLanguageTitle: 'Remove this language?',
            removeLanguageDesc: 'This will permanently delete all your progress, word bank and history for this language. This cannot be undone.',
            remove: 'Remove',
            mustSwitchFirst: 'You need to switch to another language before removing this one.',
            editProfile: 'Edit profile',
            learning: 'Learning',
            currentLevel: 'Current level',
            change: 'Change',
            appLanguage: 'App language',
            chooseAppLanguage: 'Choose the language you want the app interface in.',
            addAnotherLanguage: 'Add another language',
            switchLanguage: 'Switch learning language',
            switchLanguageDesc: 'This replaces the language you are currently learning. Support for learning multiple languages at once is coming soon.',
            stats: 'Stats',
            totalXp: 'Total XP',
            dayStreak: 'Day streak',
            wordsSaved: 'Words saved',
            lessonsDone: 'Lessons done',
            settings: 'Settings',
            notifications: 'Notifications',
            support: 'Support & feedback',
            logOut: 'Log out',
            deleteAccount: 'Delete account',
            confirmLogOut: 'Are you sure you want to log out?',
            cancel: 'Cancel',
            progressTab: 'Progress',
            languagesTab: 'Languages',
            settingsTab: 'Settings',
            subscriptionTab: 'Subscription',
            hrsStudied: 'hrs studied',
            lessonsCompleted: 'lessons completed',
            levelManagement: 'Level management',
            retakeTest: 'Retake placement test',
            retakeTestDesc: 'Get a fresh level assessment',
            changeLevelManually: 'Change level manually',
            currently: 'Currently',
        },
    },
    it: {
        nav: {
            home: 'Home',
            topics: 'Argomenti',
            grammar: 'Grammatica',
            wordbank: 'Vocabolario',
            games: 'Giochi',
        },
        home: {
            greeting: 'Ciao',
            whatToExplore: 'Cosa vuoi esplorare oggi?',
            yourTopics: 'I tuoi argomenti',
            seeAll: 'Vedi tutti',
            addLanguage: 'Aggiungi lingua',
            luna: {
                title: 'Pratica con Gabi',
                subtitle: 'Parla di qualsiasi argomento al tuo livello',
                cta: 'Parla ora',
            },
            quickPractice: 'Pratica veloce',
            vocabulary: 'Vocabolario',
            vocabularySub: 'Ripassa le parole dei tuoi argomenti',
            sentences: 'Frasi',
            sentencesSub: 'Costruisci e controlla la tua grammatica',
            pronunciation: 'Pronuncia',
            pronunciationSub: 'Pronuncia bene, Gabi ti ascolta',
            stories: 'Storie',
            storiesSub: 'Leggi ad alta voce o ascolta Gabi',
        },
        topicNames: {
            greetings: 'Saluti e presentazioni',
            numbers: 'Numeri e conteggio',
            family: 'Famiglia e relazioni',
            food: 'Cibo e bevande di base',
            home: 'Casa e vita quotidiana',
            colours: 'Colori e descrizioni',
            shopping: 'Shopping e mercato',
            gettingAround: 'Spostarsi in città',
            dailyRoutine: 'Routine quotidiana',
            weather: 'Meteo e stagioni',
            health: 'Salute e corpo',
            celebrations: 'Celebrazioni ed eventi',
            cafe: 'Al bar',
            travel: 'Viaggi e trasporti',
            restaurants: 'Cibo e ristoranti',
            culture: 'Cultura e storia',
            work: 'Lavoro e carriera',
            technology: 'Tecnologia e media',
            news: 'Notizie e attualità',
            environment: 'Ambiente e società',
            arts: 'Arte e intrattenimento',
            lawJustice: 'Legge e giustizia',
            englishGreetings: 'Saluti e presentazioni',
            englishNumbers: 'Numeri e conteggio',
            englishCafe: 'Al bar',
            englishTravel: 'Viaggi e trasporti',
            spanishGreetings: 'Saluti e presentazioni',
            spanishNumbers: 'Numeri e quantità',
            spanishFamily: 'Famiglia e relazioni',
            spanishFood: 'Cibo e ristoranti',
            spanishTravel: 'Viaggi e trasporti',
            frenchGreetings: 'Saluti e presentazioni',
            frenchNumbers: 'Numeri e cifre',
            frenchTravel: 'Viaggi e trasporti',
        },
        topicDescs: {
            greetings: 'Saluta, presentati e fai domande di base',
            numbers: 'Conta, dì l\'ora e usa quantità di base',
            family: 'Parla della tua famiglia e delle persone nella tua vita',
            food: 'Ordina cibo, parla di cosa ti piace mangiare',
            home: 'Descrivi dove vivi e le stanze di casa tua',
            colours: 'Descrivi persone, oggetti e luoghi in modo semplice',
            shopping: 'Compra cose, chiedi i prezzi e descrivi cosa ti serve',
            gettingAround: 'Usa i mezzi pubblici e chiedi indicazioni',
            dailyRoutine: 'Descrivi la tua giornata e parla delle tue abitudini',
            weather: 'Parla del tempo e delle attività stagionali',
            health: 'Descrivi come ti senti e parla con un medico',
            celebrations: 'Parla di compleanni, feste e occasioni speciali',
            cafe: 'Ordina un caffè, chiacchiera con il barista e leggi il menu',
            travel: 'Prenota biglietti, orientati in aeroporto e parla di viaggi',
            restaurants: 'Ordina un pasto completo, parla di cucina e prenota',
            culture: 'Parla di arte, storia e tradizioni culturali italiane',
            work: 'Parla del tuo lavoro, del posto di lavoro e dei tuoi obiettivi',
            technology: 'Parla di social media, app e vita digitale',
            englishGreetings: 'Saluta, presentati e fai domande di base',
            englishNumbers: 'Conta, dì l\'ora e usa quantità di base',
            englishCafe: 'Ordina un caffè, chiacchiera e leggi il menu',
            englishTravel: 'Prenota biglietti e orientati in aeroporto',
            spanishGreetings: 'Saluta, presentati e fai domande di base',
            spanishNumbers: 'Conta e parla di quantità',
            spanishFamily: 'Parla della tua famiglia e delle relazioni',
            spanishFood: 'Ordina cibo e parla di ristoranti',
            spanishTravel: 'Prenota biglietti e parla di viaggi',
            frenchGreetings: 'Saluta e presentati',
            frenchNumbers: 'Conta e parla di numeri',
            frenchTravel: 'Parla di viaggi e trasporti',
        },
        common: {
            save: 'Salva',
            cancel: 'Annulla',
            close: 'Chiudi',
            delete: 'Elimina',
            loading: 'Caricamento...',
            continue: 'Continua',
            start: 'Inizia',
            next: 'Avanti',
            back: 'Indietro',
            done: 'Fatto',
            tryAgain: 'Riprova',
        },
        onboarding: {
            targetLanguage: {
                step: 'Passo 2 di 5',
                heading: 'Quale lingua vuoi imparare?',
                subheading: 'Costruiremo tutto il tuo percorso, le lezioni e le conversazioni con l\'IA in base alla tua scelta.',
                examNote: '🎓 Tutte le lingue includono la preparazione completa agli esami da A1 a C2',
                startLearning: 'Inizia a imparare',
                chooseLanguage: 'Scegli una lingua',
                speakers: 'parlanti',
            },
            goal: {
                step: 'Passo 3 di 5',
                heading: 'Perché stai imparando?',
                subheading: 'Scegli fino a 3 motivi — questo aiuta Vozoly a personalizzare le tue lezioni e la pratica di conversazione.',
                selectAtLeastOne: 'Seleziona almeno una opzione',
                selected: 'selezionati',
                maxReached: '— massimo raggiunto',
                chooseGoal: 'Scegli il tuo obiettivo',
                continue: 'Continua',
                goals: {
                    exam: { title: 'Superare un esame', description: 'DELF, DELE, CILS, IELTS, Cambridge e altro — ho bisogno di un certificato ufficiale' },
                    travel: { title: 'Viaggi e vacanze', description: 'Voglio cavarmela con sicurezza quando visito o vivo all\'estero' },
                    work: { title: 'Lavoro e carriera', description: 'Ho bisogno della lingua per il mio lavoro, business o vita professionale' },
                    family: { title: 'Famiglia e origini', description: 'Voglio connettermi con le mie radici o comunicare con la famiglia' },
                    culture: { title: 'Cultura e divertimento', description: 'Amo la lingua, la musica, il cibo o i film e voglio approfondire' },
                    brain: { title: 'Mantenere la mente attiva', description: 'Imparare una lingua è una delle cose migliori che puoi fare per la tua mente' },
                },
            },
            path: {
                step: 'Passo 4 di 5',
                heading: 'Come vuoi iniziare?',
                subheading: 'Dobbiamo conoscere il tuo livello attuale così Vozoly può collocarti nel punto di partenza giusto.',
                recommended: 'Consigliato',
                testTitle: 'Fai il test di valutazione',
                testDesc: 'Richiede solo 8-12 minuti e ti dà un risultato preciso su grammatica, ascolto, conversazione e scrittura.',
                testDetail1: '⏱ 8–12 minuti in media',
                testDetail2: '🎯 Collocazione CEFR precisa',
                testDetail3: '📈 Si adatta alle tue risposte in tempo reale',
                testDetail4: '✅ Può finire prima se il tuo livello è chiaro',
                beginnerTitle: 'Sono un principiante assoluto',
                beginnerDesc: 'Non hai mai studiato questa lingua? Nessun test necessario — ti faremo iniziare da A1 e costruiremo le tue basi da zero. Parlerai le tue prime parole oggi stesso.',
                beginnerDetail1: '🌱 Inizia da zero — senza pressione',
                beginnerDetail2: '🏁 Collocato automaticamente ad A1',
                manualTitle: 'Conosco già il mio livello',
                manualDesc: 'Se hai già studiato questa lingua e conosci il tuo livello CEFR, salta il test e scegli direttamente il tuo punto di partenza — da A1 a C2.',
                manualDetail1: '⚡ Inizia subito',
                manualDetail2: '🔤 A1 · A2 · B1 · B2 · C1 · C2',
                note: '💡 Puoi sempre rifare il test di valutazione o cambiare il tuo livello più tardi dal tuo profilo.',
                startTest: 'Inizia il test di valutazione',
                startBeginner: 'Inizia da A1',
                chooseLevel: 'Scegli il mio livello',
                chooseHow: 'Scegli come iniziare',
            },
            placementTest: {
                listeningCheck: 'Verifica ascolto',
                speakingCheck: 'Verifica orale',
                writingCheck: 'Verifica scritta',
                preparingTest: 'Preparazione del test...',
                preparingListening: 'Preparazione del test di ascolto...',
                listenCarefully: 'Ascolta attentamente — risponderai a {count} domande sullo stesso brano.',
                playAudio: 'Riproduci audio',
                playing: 'Riproduzione...',
                question: 'Domanda',
                of: 'di',
                listeningError: 'Qualcosa è andato storto nel caricamento del test di ascolto. Puoi continuare senza.',
                continue: 'Continua',
                checkAnswer: 'Controlla risposta',
                nextQuestion: 'Prossima domanda',
                abandonTitle: 'Abbandonare il test?',
                abandonDesc: 'I tuoi progressi andranno persi e dovrai ricominciare da capo.',
                keepGoing: 'Continua così',
                abandon: 'Abbandona',
                speakingIntro: 'Parla per circa 30-60 secondi. Parla in modo naturale — stiamo ascoltando come usi la lingua, non cosa dici del tuo livello.',
                writingIntro: 'Scrivi una breve risposta qui sotto. Guardiamo come usi la lingua, non cosa dici del tuo livello.',
                analyzingSpeaking: 'Analisi del tuo parlato...',
                analyzingWriting: 'Analisi della tua scrittura...',
                transcribing: 'Trascrizione di quello che hai detto...',
                yourPrompt: 'Il tuo argomento',
                holdToSpeak: 'Tieni premuto per parlare',
                releaseToStop: 'rilascia per fermare',
                slideToLock: 'scorri per bloccare',
                recording: 'Registrazione',
                skipStep: 'Salta questo passaggio',
                gotItSpeaking: 'Fatto! Grazie per aver parlato con noi.',
                gotItWriting: 'Fatto! Grazie per aver scritto per noi.',
                writeHere: 'Scrivi qui nella lingua che stai imparando...',
                submitWriting: 'Invia la scrittura',
                seeMyResult: 'Vedi il mio risultato finale',
                calibrating: 'Calibrazione del tuo livello...',
                yourLevel: 'Il tuo livello',
                adjustedLevel: 'Livello modificato',
                doesThisFeel: 'Ti sembra corretto?',
                feelsAccurate: 'Sì, mi sembra accurato',
                lowerLevel: 'Penso di essere di un livello inferiore',
                higherLevel: 'Penso di essere di un livello superiore',
                resultDisclaimer: 'Questo risultato combina le tue prestazioni in grammatica, ascolto, produzione orale e scritta. Continua a esercitarti con Gabi per affinarlo ulteriormente.',
                retakeNote: 'Puoi rifare questo test o modificare il tuo livello in qualsiasi momento dal tuo profilo.',
                adjustedFrom: 'Modificato da {from} a {to} in base al tuo feedback.',
                changeAnytime: 'Puoi sempre cambiarlo dal tuo profilo.',
                loading: 'Caricamento...',
                questionNum: 'Domanda',
                movingOn: 'Passaggio automatico alla prossima domanda...',
                changeAnswer: 'Cambia risposta',
                next: 'Avanti',
                seeMyResultGrammar: 'Vedi il mio risultato',
                skipWait: 'Salta attesa',
                correct: 'Corrette',
                answered: 'Risposte',
                placedAt: 'Collocato a',
                saveResultsBtn: 'Salva i risultati e crea account',
            },
            chooseLevel: {
                heading: 'Qual è il tuo livello attuale?',
                subheading: 'Sii onesto — scegliere il livello giusto significa che le tue lezioni saranno stimolanti ma mai eccessive.',
                note: '💡 Non sei sicuro? Fai invece il test di valutazione — richiede solo 8-12 minuti e ti dà un risultato preciso.',
                takeTest: 'Fai il test',
                saveLevel: 'Salva livello',
                startAt: 'Inizia da',
                chooseLevel: 'Scegli il tuo livello',
                levels: {
                    A1: { label: 'Principiante', desc: 'Conosco alcune parole ma niente di solido ancora' },
                    A2: { label: 'Elementare', desc: 'Riesco a gestire conversazioni semplici e la grammatica di base' },
                    B1: { label: 'Intermedio', desc: 'Riesco ad esprimermi su argomenti familiari con una certa sicurezza' },
                    B2: { label: 'Intermedio superiore', desc: 'Riesco a discutere argomenti complessi e capire la maggior parte dei contenuti' },
                    C1: { label: 'Avanzato', desc: 'Parlo fluentemente e riesco a gestire situazioni professionali impegnative' },
                    C2: { label: 'Padronanza', desc: 'Ho una competenza quasi madrelingua e capisco tutto' },
                },
            },
        },
        topics: {
            title: 'Argomenti',
            searchPlaceholder: 'Cerca argomenti...',
            allLevels: 'Tutti i livelli',
            noTopicsFound: 'Nessun argomento trovato',
            tryDifferentSearch: 'Prova un termine di ricerca diverso',
            locked: 'Bloccato',
            levelNames: {
                A1: 'Principiante',
                A2: 'Elementare',
                B1: 'Intermedio',
                B2: 'Intermedio superiore',
                C1: 'Avanzato',
                C2: 'Padronanza',
            },
        },
        grammar: {
            title: 'Grammatica',
            searchPlaceholder: 'Cerca un verbo...',
            lookUp: 'Cerca',
            intro: 'Tocca qualsiasi verbo per vedere la sua coniugazione completa in ogni tempo e salvare le forme nel tuo vocabolario. Non trovi il verbo che cerchi? Cerca',
            introBold: 'qualsiasi',
            introEnd: 'verbo italiano qui sopra — non solo quelli elencati sotto.',
            verbs: 'verbi',
        },
        profile: {
            title: 'Profilo',
            myProfile: 'Il mio profilo',
            freePlan: 'Piano gratuito',
            active: 'Attivo',
            tapToSwitch: 'Tocca per cambiare',
            learnMultiple: 'Impara più lingue — passa da una all\'altra in qualsiasi momento',
            account: 'Account',
            displayName: 'Nome visualizzato',
            emailAddress: 'Indirizzo email',
            changePassword: 'Cambia password',
            updatePassword: 'Aggiorna la tua password',
            prefs: 'Preferenze',
            notificationsDesc: 'Promemoria giornalieri e avvisi sui giorni consecutivi',
            soundEffects: 'Effetti sonori',
            on: 'Attivo',
            helpSupport: 'Aiuto e supporto',
            helpSupportDesc: 'Domande frequenti e contattaci',
            termsPrivacy: 'Termini e informativa sulla privacy',
            termsPrivacyDesc: 'Leggi i nostri termini di servizio',
            dangerZone: 'Zona pericolosa',
            deleteMyAccount: 'Elimina il mio account',
            deleteMyAccountDesc: 'Elimina permanentemente il tuo account e tutti i dati',
            deleteAccountTitle: 'Eliminare il tuo account?',
            deleteAccountWarning: 'Questo eliminerà permanentemente il tuo account, tutti i tuoi progressi, il vocabolario e la cronologia delle conversazioni. Questa azione non può essere annullata.',
            enterPasswordConfirm: 'Inserisci la tua password per confermare:',
            yourPassword: 'La tua password',
            deleting: 'Eliminazione in corso...',
            deleteAccountBtn: 'Elimina account',
            currentPlanFree: 'Hai accesso ai primi 3 argomenti per livello e a 2 sessioni con Gabi al giorno.',
            unlockEverything: 'Sblocca tutto',
            unlockDesc: 'Ottieni accesso completo a tutti gli argomenti, conversazioni illimitate con Gabi, preparazione agli esami e altro.',
            featureAllTopics: 'Tutti gli argomenti ad ogni livello',
            featureUnlimitedLuna: 'Conversazioni illimitate con Gabi',
            featureExamPrep: 'Preparazione completa agli esami',
            featureUnlimitedWordBank: 'Vocabolario illimitato',
            featureFullStats: 'Statistiche complete e monitoraggio Cambridge',
            seePlans: 'Vedi i piani',
            removeLanguageTitle: 'Rimuovere questa lingua?',
            removeLanguageDesc: 'Questo eliminerà permanentemente tutti i tuoi progressi, il vocabolario e la cronologia per questa lingua. Questa azione non può essere annullata.',
            remove: 'Rimuovi',
            mustSwitchFirst: 'Devi passare a un\'altra lingua prima di rimuovere questa.',
            editProfile: 'Modifica profilo',
            learning: 'Sto imparando',
            currentLevel: 'Livello attuale',
            change: 'Cambia',
            appLanguage: 'Lingua dell\'app',
            chooseAppLanguage: 'Scegli la lingua in cui vuoi l\'interfaccia dell\'app.',
            addAnotherLanguage: 'Aggiungi un\'altra lingua',
            switchLanguage: 'Cambia lingua di apprendimento',
            switchLanguageDesc: 'Questo sostituisce la lingua che stai attualmente imparando. Il supporto per imparare più lingue contemporaneamente arriverà presto.',
            stats: 'Statistiche',
            totalXp: 'XP totali',
            dayStreak: 'Giorni consecutivi',
            wordsSaved: 'Parole salvate',
            lessonsDone: 'Lezioni completate',
            settings: 'Impostazioni',
            notifications: 'Notifiche',
            support: 'Supporto e feedback',
            logOut: 'Esci',
            deleteAccount: 'Elimina account',
            confirmLogOut: 'Sei sicuro di voler uscire?',
            cancel: 'Annulla',
            progressTab: 'Progressi',
            languagesTab: 'Lingue',
            settingsTab: 'Impostazioni',
            subscriptionTab: 'Abbonamento',
            hrsStudied: 'ore studiate',
            lessonsCompleted: 'lezioni completate',
            levelManagement: 'Gestione livello',
            retakeTest: 'Rifai il test di valutazione',
            retakeTestDesc: 'Ottieni una nuova valutazione del livello',
            changeLevelManually: 'Cambia livello manualmente',
            currently: 'Attualmente',
        },
    },
    es: {
        nav: {
            home: 'Inicio',
            topics: 'Temas',
            grammar: 'Gramática',
            wordbank: 'Vocabulario',
            games: 'Juegos',
        },
        home: {
            greeting: 'Hola',
            whatToExplore: '¿Qué quieres explorar hoy?',
            yourTopics: 'Tus temas',
            seeAll: 'Ver todo',
            addLanguage: 'Añadir idioma',
            luna: {
                title: 'Practica con Gabi',
                subtitle: 'Habla de cualquier tema a tu nivel',
                cta: 'Habla ahora',
            },
            quickPractice: 'Práctica rápida',
            vocabulary: 'Vocabulario',
            vocabularySub: 'Repasa palabras de tus temas',
            sentences: 'Frases',
            sentencesSub: 'Construye y revisa tu gramática',
            pronunciation: 'Pronunciación',
            pronunciationSub: 'Pronuncia bien, Gabi escucha',
            stories: 'Historias',
            storiesSub: 'Lee en voz alta o escucha a Gabi',
        },
        common: {
            save: 'Guardar',
            cancel: 'Cancelar',
            close: 'Cerrar',
            delete: 'Eliminar',
            loading: 'Cargando...',
            continue: 'Continuar',
            start: 'Empezar',
            next: 'Siguiente',
            back: 'Atrás',
            done: 'Hecho',
            tryAgain: 'Intentar de nuevo',
        },
    },
    fr: {
        nav: {
            home: 'Accueil',
            topics: 'Sujets',
            grammar: 'Grammaire',
            wordbank: 'Vocabulaire',
            games: 'Jeux',
        },
        home: {
            greeting: 'Bonjour',
            whatToExplore: "Qu'allez-vous explorer aujourd'hui ?",
            yourTopics: 'Vos sujets',
            seeAll: 'Voir tout',
            addLanguage: 'Ajouter une langue',
            luna: {
                title: 'Pratiquer avec Gabi',
                subtitle: 'Parlez de n\'importe quel sujet à votre niveau',
                cta: 'Parler maintenant',
            },
            quickPractice: 'Pratique rapide',
            vocabulary: 'Vocabulaire',
            vocabularySub: 'Révisez les mots de vos sujets',
            sentences: 'Phrases',
            sentencesSub: 'Construisez et vérifiez votre grammaire',
            pronunciation: 'Prononciation',
            pronunciationSub: 'Prononcez bien, Gabi écoute',
            stories: 'Histoires',
            storiesSub: 'Lisez à voix haute ou écoutez Gabi',
        },
        common: {
            save: 'Enregistrer',
            cancel: 'Annuler',
            close: 'Fermer',
            delete: 'Supprimer',
            loading: 'Chargement...',
            continue: 'Continuer',
            start: 'Commencer',
            next: 'Suivant',
            back: 'Retour',
            done: 'Terminé',
            tryAgain: 'Réessayer',
        },
    },
    pt: {
        nav: {
            home: 'Início',
            topics: 'Tópicos',
            grammar: 'Gramática',
            wordbank: 'Vocabulário',
            games: 'Jogos',
        },
        home: {
            greeting: 'Olá',
            whatToExplore: 'O que você vai explorar hoje?',
            yourTopics: 'Seus tópicos',
            seeAll: 'Ver tudo',
            addLanguage: 'Adicionar idioma',
            luna: {
                title: 'Praticar com Gabi',
                subtitle: 'Fale sobre qualquer tópico no seu nível',
                cta: 'Falar agora',
            },
            quickPractice: 'Prática rápida',
            vocabulary: 'Vocabulário',
            vocabularySub: 'Revise palavras dos seus tópicos',
            sentences: 'Frases',
            sentencesSub: 'Construa e verifique sua gramática',
            pronunciation: 'Pronúncia',
            pronunciationSub: 'Pronuncie corretamente, Gabi escuta',
            stories: 'Histórias',
            storiesSub: 'Leia em voz alta ou ouça a Gabi',
        },
        common: {
            save: 'Salvar',
            cancel: 'Cancelar',
            close: 'Fechar',
            delete: 'Excluir',
            loading: 'Carregando...',
            continue: 'Continuar',
            start: 'Começar',
            next: 'Próximo',
            back: 'Voltar',
            done: 'Concluído',
            tryAgain: 'Tentar novamente',
        },
    },
};